import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, ArrowRight, Loader2, ShieldCheck, Clock4, CheckCircle2, Download, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MarkTransferSentModal from "@/components/dashboard/MarkTransferSentModal";

/**
 * /dashboard/orders — buyer's lead-pack orders list (iter 55).
 * Active packs show progress towards their lead quota.
 */
export default function ClientOrders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOrder, setReportOrder] = useState(null); // order being reported in the mark-transfer-sent modal
  const nav = useNavigate();

  const refresh = async () => {
    try {
      const { data } = await api.get("/client/lead-packs");
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="size-5 animate-spin mr-2" /> Loading orders…
      </div>
    );
  }

  const active = data?.active || [];
  const awaiting = data?.awaiting_payment || [];
  const completed = data?.completed || [];
  const summary = data?.summary || {};

  // iter86: when the user has NO active deliveries AND NO pending bank transfer,
  // surface a single clean CTA — not two duplicate "build order" buttons.
  // When they DO have a pending bank-transfer order, the empty-state hint is
  // redundant noise (they're literally mid-onboarding).
  const showEmptyState = active.length === 0 && awaiting.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Orders</h1>
          <p className="mt-1 text-[13.5px] text-slate-600">Your lead packs — live delivery progress and history.</p>
        </div>
        <Button onClick={() => nav("/buy-leads")} className="rounded-full h-10" data-testid="orders-new-btn">
          {active.length === 0 && awaiting.length === 0 ? "Place your first order" : "Order more leads"} <ArrowRight className="size-4" />
        </Button>
      </div>

      {/* Summary */}
      <div className="grid sm:grid-cols-3 gap-4">
        <SummaryCard label="Active orders" value={active.length} />
        <SummaryCard label="Leads remaining" value={(summary.total_remaining || 0).toLocaleString()} />
        <SummaryCard label="Lifetime spend" value={`£${(summary.total_lifetime_spend || 0).toLocaleString()}`} />
      </div>

      {/* Awaiting bank transfer — surfaced FIRST so newly-signed-up buyers see
          their pending invoice + download link immediately. */}
      {awaiting.length > 0 && (
        <Section
          title="Awaiting bank transfer"
          subtitle="Your campaign is reserved — we'll activate it as soon as funds clear."
        >
          <div className="space-y-3">
            {awaiting.map((p) => (
              <OrderRow key={p.id} p={p} awaiting onMarkTransferSent={() => setReportOrder(p)} />
            ))}
          </div>
        </Section>
      )}

      {/* Active */}
      {active.length > 0 && (
        <Section title="Active deliveries" subtitle={`${active.length} order${active.length === 1 ? "" : "s"} currently delivering`}>
          <div className="space-y-3">
            {active.map((p) => <OrderRow key={p.id} p={p} />)}
          </div>
        </Section>
      )}

      {/* Empty state — only when no active AND no awaiting */}
      {showEmptyState && (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center">
            <Package className="mx-auto size-10 text-slate-300" strokeWidth={1.4} />
            <h3 className="mt-4 text-base font-semibold text-slate-900">No orders yet</h3>
            <p className="mt-1.5 text-[13px] text-slate-600 max-w-md mx-auto">
              Pick a sector, set your volume, choose delivery, and pay once. Use the button at the top of this page to get started.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <Section title="Completed" subtitle="All leads delivered">
          <div className="space-y-3">
            {completed.map((p) => <OrderRow key={p.id} p={p} done />)}
          </div>
        </Section>
      )}

      {/* Mark transfer sent modal */}
      <MarkTransferSentModal
        open={!!reportOrder}
        order={reportOrder}
        onClose={() => setReportOrder(null)}
        onReported={() => refresh()}
      />
    </div>
  );
}

/* atoms */
function SummaryCard({ label, value }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="py-5 px-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="mt-2 text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>
        <p className="text-[12.5px] text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function OrderRow({ p, done, awaiting, onMarkTransferSent }) {
  const pct = p.progress_pct || 0;
  const reported = !!p.transfer_sent_at;

  const openInvoicePdf = async () => {
    if (!p.invoice_id) return;
    try {
      const { data } = await api.get(`/invoices/${p.invoice_id}/share-link`);
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open PDF");
    }
  };

  // Pretty date for the reported-at badge (e.g. "4 Jun")
  const reportedDateLabel = reported
    ? new Date(p.transfer_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : "";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`order-${p.id.slice(0, 8)}`}>
      <div className="flex items-start gap-5 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[15px] font-semibold text-slate-900">
              {p.quantity_ordered.toLocaleString()} × {p.sector_name}
            </span>
            {done && <CheckCircle2 className="size-4 text-emerald-500" />}
            {awaiting && <Clock4 className="size-4 text-amber-500" />}
            <ShieldCheck className="size-4 text-emerald-500" strokeWidth={1.7} />
          </div>
          <div className="text-[12px] text-slate-500">
            Order #{p.id.slice(0, 8)} · 100% exclusive · {p.delivery_method.replace(/_/g, " ")} · £{Number(p.cpl_gbp).toFixed(2)}/lead
          </div>

          {/* iter86: invoice link on awaiting rows */}
          {awaiting && p.invoice_number && (
            <div className="mt-2 inline-flex items-center gap-2 text-[11.5px] text-slate-600">
              <FileText className="size-3.5 text-slate-500" />
              <span className="font-mono">{p.invoice_number}</span>
              <button
                type="button"
                onClick={openInvoicePdf}
                className="inline-flex items-center gap-1 text-[#0055FF] hover:underline font-medium"
                data-testid={`order-invoice-pdf-${p.id.slice(0, 8)}`}
              >
                <Download className="size-3" /> Download invoice
              </button>
            </div>
          )}

          {/* iter87: mark-transfer-sent CTA / confirmation */}
          {awaiting && (
            <div className="mt-3">
              {reported ? (
                <div
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[11.5px] font-medium text-emerald-800"
                  data-testid={`order-transfer-reported-${p.id.slice(0, 8)}`}
                >
                  <CheckCircle2 className="size-3.5" strokeWidth={2} />
                  Transfer reported · {reportedDateLabel}
                  {p.bank_reference && (
                    <span className="ml-1 font-mono text-emerald-700/80">· ref {p.bank_reference}</span>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onMarkTransferSent?.()}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[11.5px] font-semibold px-3 py-1.5 transition-colors"
                  data-testid={`order-mark-transfer-sent-${p.id.slice(0, 8)}`}
                >
                  <Send className="size-3" strokeWidth={2} /> Mark transfer sent
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[18px] font-semibold tabular-nums text-slate-900">£{Number(p.total_gbp).toLocaleString()}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-widest">{awaiting ? (reported ? "Funds in transit" : "Awaiting payment") : done ? "Completed" : `${p.remaining} remaining`}</div>
        </div>
      </div>
      {!awaiting && (
        <div className="mt-4">
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#0055FF]" style={{ width: `${Math.min(pct, 100)}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-500">
            <span>{p.quantity_delivered} delivered</span>
            <span>{Math.round(pct)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}
